import { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import { Controller } from "react-hook-form";
import FormBorder from "../components/formBorder";
import SelectElement from "../components/event-selection/selectElement";
import Input from "../components/event-selection/Input";
import Checkboxes from "../components/event-selection/checkboxes";
import SelectAndQmark from "../components/event-selection/select&Qmark";
import TextArea from "../components/event-selection/textArea";
import IncidentService from "../services/incidentsService";
import { toast } from "react-toastify";
import Loaing from "../icons/loaing";
import postEvent from "../services/eventService";
import ConfirmModal from "../components/ConfirmModal";
import CalendarIcon from "../icons/calendar";
import CustomCheckboxes from "../components/event-selection/customCheckboxes";
import moment from "moment";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { IncidentsPDF } from "../components/dashboard/incidents-pdf";

export type IncidentsInputeTypes = {
  reporting: string;
  eventDate: string;
  eventTime: Date;
  eventMoment: string;
  eventLocation: string;
  precisionTypeEvent: string;
  eventDescription: string;
  consequences: string;
  emsContacted: string;
  hospitalization: string;
  insuranceBeNotified: string;
  insuranceBeenNotified: string;
  localAuthorityBeNotified: string;
  localAuthorityBeenNotified: string;
  parentsBeenInformed: string;
  securityLevel: string;
  aftercare: string;
  childAbsence: string;
  supervisionRateRespected: string;
  usualTeam: string;
  professionalNames: string;
  immediateCorrectiveActionsArray?: string[];
  immediateCorrectiveActions: string;
  riskRelatedEventArray?: string[];
  riskRelatedEvent: string;
  eventAvoidedSafer: string;
  recurrence: string;
  ageOfChild: string;
  printeventDate?: boolean;
  printeventTime?: boolean;
  printeventMoment?: boolean;
  printeventLocation?: boolean;
  printprecisionTypeEvent?: boolean;
  printeventDescription?: boolean;
  printconsequences?: boolean;
  printemsContacted?: boolean;
  printhospitalization?: boolean;
  printinsuranceBeNotified?: boolean;
  printinsuranceBeenNotified?: boolean;
  printlocalAuthorityBeNotified?: boolean;
  printlocalAuthorityBeenNotified?: boolean;
  printparentsBeenInformed?: boolean;
  printsecurityLevel?: boolean;
  printaftercare?: boolean;
  printchildAbsence?: boolean;
  printsupervisionRateRespected?: boolean;
  printusualTeam?: boolean;
  printprofessionalNames?: boolean;
  printimmediateCorrectiveActionsArray?: boolean;
  printimmediateCorrectiveActions?: boolean;
  printriskRelatedEventArray?: boolean;
  printriskRelatedEvent?: boolean;
  printeventAvoidedSafer?: boolean;
  printrecurrence?: boolean;
  printageOfChild?: boolean;
};

const schema = yup.object({
  eventDate: yup.string().required(),
  eventTime: yup.string().required(),
  eventMoment: yup.string().required(),
  eventLocation: yup.string().required(),
  precisionTypeEvent: yup.string().required(),
  eventDescription: yup.string().required(),
  consequences: yup.string().required(),
  emsContacted: yup.string().required(),
  hospitalization: yup.string().required(),
  insuranceBeNotified: yup.string().required(),
  insuranceBeenNotified: yup.string().required(),
  localAuthorityBeNotified: yup.string().required(),
  localAuthorityBeenNotified: yup.string().required(),
  parentsBeenInformed: yup.string().required(),
  securityLevel: yup.string().required(),
  aftercare: yup.string().required(),
  childAbsence: yup.string().required(),
  supervisionRateRespected: yup.string().required(),
  usualTeam: yup.string().required(),
  professionalNames: yup.string().required(),
  immediateCorrectiveActionsArray: yup
    .array()
    .min(1, "immediateCorrectiveActions is a required field!")
    .test(
      "no-empty-strings",
      "Array cannot contain empty string values",
      (value) => {
        return new Promise((resolve) => {
          resolve(value?.every((item) => item !== "") ?? false);
        });
      }
    ),
  riskRelatedEventArray: yup
    .array()
    .min(1, "riskRelatedEvent is a required field!")
    .test(
      "no-empty-strings",
      "Array cannot contain empty string values",
      (value) => {
        return new Promise((resolve) => {
          resolve(value?.every((item) => item !== "") ?? false);
        });
      }
    ),
  eventAvoidedSafer: yup.string().required(),
  recurrence: yup.string().required(),
  ageOfChild: yup.string().required(),
});

const Incidents = () => {
  const [loading, setLoading] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [startDate, setStartDate] = useState<null | Date>(null);
  const [pdfData, setPdfData] = useState<any>(null);

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<IncidentsInputeTypes>({ resolver: yupResolver(schema) });

  const handleDownload: SubmitHandler<IncidentsInputeTypes> = async (data) => {
    data.riskRelatedEvent =
      data.riskRelatedEventArray?.map((value) => value).join("-,-") ?? "";
    data.printriskRelatedEvent = data.printriskRelatedEventArray;
    data.immediateCorrectiveActions =
      data.immediateCorrectiveActionsArray?.map((value) => value).join("-,-") ??
      "";
    data.eventDate = moment(startDate).format("YYYY-MM-DD");
    data.printimmediateCorrectiveActions =
      data.printimmediateCorrectiveActionsArray;

    setPdfData(data);
  };

  const resetForm = () => {
    setConfirmModalVisible(false);
    reset();
    setPdfData(null);
  };

  const onSubmit: SubmitHandler<IncidentsInputeTypes> = async (data) => {
    setLoading(true);
    try {
      const {
        printaftercare,
        printageOfChild,
        printchildAbsence,
        printconsequences,
        printemsContacted,
        printeventAvoidedSafer,
        printeventDate,
        printeventDescription,
        printeventLocation,
        printeventMoment,
        printeventTime,
        printhospitalization,
        printimmediateCorrectiveActionsArray,
        printimmediateCorrectiveActions,
        printinsuranceBeNotified,
        printinsuranceBeenNotified,
        printlocalAuthorityBeNotified,
        printlocalAuthorityBeenNotified,
        printparentsBeenInformed,
        printprecisionTypeEvent,
        printprofessionalNames,
        printrecurrence,
        printriskRelatedEvent,
        printsecurityLevel,
        printsupervisionRateRespected,
        printusualTeam,
        printriskRelatedEventArray,
        ...rest
      } = data;
      rest.immediateCorrectiveActions =
        rest.immediateCorrectiveActionsArray
          ?.map((value) => value)
          .join("-,-") ?? "";
      rest.riskRelatedEvent =
        rest.riskRelatedEventArray?.map((value) => value).join("-,-") ?? "";
      rest.eventDate = moment(startDate).format("YYYY-MM-DD");
      delete rest.immediateCorrectiveActionsArray;
      delete rest.riskRelatedEventArray;
      const response = await IncidentService({ ...rest });

      if (response.message === "created") {
        // TODO: needs test
        const eventpost = await postEvent({
          eventId: response.data._id,
          severity: data?.securityLevel,
          eventDetailsDate: response.data?.eventDate,
        });
        if ((eventpost.message = "created")) {
          toast.success("Incident created succesfully!");
          setLoading(false);
          navigate("/confirm");
        }
      }
      if (response.message === "created") {
        navigate("/confirm");
      }
    } catch (error: any) {
      setLoading(false);
      toast.error(error);
    }
  };

  const { t } = useTranslation();

  return (
    <section className="relative font-Poppins px-[20px] lg:px-0">
      <div>
        <div className="grid gap-3 lg:mx-10">
          <div className="w-16 h-16">
            <img
              src="/images/report.png"
              alt="!"
              className="max-w-full h-auto"
            />
          </div>
          <h1 className="text-4xl font-bold">
            <Trans t={t}>Incidents</Trans>
          </h1>
          <p className="text-xl text-[#6B6B6B]">
            <Trans t={t}>incidents_description</Trans>
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-end gap-[15px] lg:mx-[20px] my-5 lg:my-10">
          <button
            type="button"
            className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
            onClick={() => setConfirmModalVisible(true)}
          >
            <Trans t={t}>Reset</Trans>
          </button>
          {pdfData ? (
            <PDFDownloadLink
              className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
              document={<IncidentsPDF formData={pdfData ?? {}} />}
              fileName={"incident.pdf"}
            >
              {({ blob, url, loading, error }) =>
                loading ? "loading..." : "Download PDF"
              }
            </PDFDownloadLink>
          ) : (
            <button
              className="px-5 py-2 rounded-full text-white bg-[#1E408E]"
              onClick={handleSubmit(handleDownload)}
            >
              <Trans>Generate PDF</Trans>
            </button>
          )}
        </div>
        <FormBorder>
          <div className="grid gap-5">
            <div className="grid md:grid-cols-2 grid-cols-1 w-full gap-[5px] md:gap-0 ">
              <label
                htmlFor="eventDate"
                className="font-normal text-xl text-[#1E408E]"
              >
                <Trans t={t}>Incident date</Trans>
              </label>
              <div className="grid pr-10">
                <div className="flex flex-row ">
                  <div className="mr-4 bg-white w-full flex items-center border border-[#8692A6] h-12 p-2 rounded">
                    <Controller
                      control={control}
                      // register={register}
                      {...register(`eventDate`)}
                      name="eventDate"
                      render={({ field }) => (
                        <DatePicker
                          filterDate={(d) => {
                            return new Date() > d;
                          }}
                          selectsRange={false}
                          onChange={(date: Date) => {
                            field.onChange(date?.toDateString());
                            setStartDate(date);
                          }}
                          placeholderText="mm/dd/yyyy"
                          selected={startDate}
                          dateFormat={"dd MMM yyyy"}
                          isClearable={false}
                          className="w-full border-none p-0 bg-white focus:outline-none"
                        />
                      )}
                    />
                    <CalendarIcon />
                  </div>
                  <input
                    type="checkbox"
                    {...register("printeventDate")}
                    defaultChecked
                    id={"printeventDate"}
                  />
                </div>
                {errors["eventDate"] && (
                  <p className="text-red-500 mt-2 text-sm">
                    {errors["eventDate"]?.message}
                  </p>
                )}
              </div>
            </div>

            {/* <Input
              type="Date"
              register={register}
              errors={errors}
              name="eventDate"
              label="Event date"
              placeholder=""
              translateId="Event date"
            /> */}
            <Input
              type="Time"
              register={register}
              errors={errors}
              name="eventTime"
              label="Incident time"
              placeholder="hh:mm"
              translateId="Event time"
              printEnabled
            />
            <SelectElement
              register={register}
              errors={errors}
              name="eventMoment"
              label="Incident moment"
              printEnabled
              options={[
                "Play time",
                "Sleep time",
                "Changing/care time",
                "Meal time",
                "Reception time (morning and evening)",
              ]}
              translateId="Event moment"
            />
            <SelectElement
              printEnabled
              register={register}
              errors={errors}
              name="eventLocation"
              label="Incident location"
              options={[
                "Interior (School indoor premise)",
                "Exterior space (School outdoor premise)",
                "Outside the establishment",
              ]}
              translateId="Event location"
            />
            <SelectElement
              printEnabled
              register={register}
              errors={errors}
              name="ageOfChild"
              label="Age of the child involved in the incident"
              options={[
                "Less than 1 year",
                "1 to 2 years",
                "2 to 3 years",
                "3 to 4 years",
                "4 to 5 years",
                "5 to 6 years",
                "More than 6 years old",
              ]}
              translateId="Aga of the child"
            />
            <Checkboxes
              register={register}
              errors={errors}
              name="precisionTypeEvent"
              printEnabled
              options={[
                { text: "High fall" },
                { text: "Fall from a small height" },
                { text: "Fall of object.furniture on the child" },
                { text: "Shock against a wall / furniture / object" },
                { text: "Shock between people / bites" },
                { text: "Fingers / feet caught" },
                { text: "Ingestion of foreign body / toxic product" },
                { text: "Insect bite" },
                { text: "Inapproriate touch" },
              ]}
              label="Precision on the type of incident"
              translateId="Precision on the type of event"
            />
            <TextArea
              printEnabled
              register={register}
              errors={errors}
              name="eventDescription"
              label="Short description about how the incident happened (factual & taken care of elements)"
              translateId="eventDescription"
            />
            <Checkboxes
              register={register}
              errors={errors}
              name="consequences"
              printEnabled
              options={[
                { text: "Slight abrasion" },
                { text: "Deep scratch" },
                { text: "Superficial cut" },
                { text: "Deep cut" },
                { text: "Bumps/blues" },
                { text: "Head trauma" },
                { text: "Surface bite with broken skin" },
                { text: "Deep bite with open wound" },
                { text: "Damaged teeth/gums" },
                { text: "Limb section, amputation" },
                { text: "Fracture" },
                { text: "Choking" },
                { text: "Swelling" },
                { text: "Others" },
              ]}
              label="Injuries arising from the incident"
              translateId="Consequences"
            />
            <SelectElement
              register={register}
              printEnabled
              errors={errors}
              name="emsContacted"
              label="Any Emergency Medical Services provided"
              options={["Yes", "No"]}
              translateId="EMS contacted"
            />
            <SelectElement
              register={register}
              printEnabled
              errors={errors}
              name="hospitalization"
              options={[
                "No",
                "Less than 24H",
                "Less than 36H",
                "Less than 48H",
                "More than 48H",
              ]}
              label="Hospitalization (including emergency room)"
              translateId="hospitalization"
            />

            <SelectElement
              register={register}
              printEnabled
              errors={errors}
              name="childAbsence"
              label="Child absence"
              options={[
                // "No absence",
                // "Returned the next day",
                // "Absence of 1 to 3 days",
                // "Absence for more than 3 days",
                // "Parents have terminated the service",
                "No absence or maximum 1 day",
                "Maximum 2 days",
                "Maximum 3 days",
                "Maximum 1 week",
                "More than one week",
                "Parents terminated the care agreement",
              ]}
              translateId="Child absence"
            />
            <SelectElement
              register={register}
              printEnabled
              errors={errors}
              name="aftercare"
              label="Aftercare"
              options={[
                "No",
                "Yes, for few days",
                "Yes, for few weeks",
                "Yes, for few months",
                "Yes, for more than a year",
              ]}
              translateId="Aftercare"
            />
            <SelectAndQmark
              printEnabled
              register={register}
              errors={errors}
              name="securityLevel"
              label="Severity level"
              options={[0, 1, 2, 3, 4, 5]}
              translateId="Severity level"
              i18nKey="Help Box - Incidents - Severity level"
            />

            <SelectElement
              printEnabled
              register={register}
              errors={errors}
              name="parentsBeenInformed"
              label="parentsBeenInformed"
              options={["Yes", "No"]}
              translateId="parentsBeenInformed"
            />

            <SelectAndQmark
              printEnabled
              register={register}
              errors={errors}
              name="insuranceBeNotified"
              label="Will insurance claim be filed?"
              options={["Yes", "No"]}
              translateId="insuranceBeNotified"
              i18nKey="Help Box - Incidents - Does our Company Insurance need to be informed ?"
            />
            <SelectElement
              printEnabled
              register={register}
              errors={errors}
              name="insuranceBeenNotified"
              label="Has the insurance claim been filed?"
              options={["Yes", "No"]}
              translateId="insuranceBeenNotified"
            />
            <SelectAndQmark
              printEnabled
              register={register}
              errors={errors}
              name="localAuthorityBeNotified"
              label="Should the local authority be notified?"
              options={["Yes", "No"]}
              translateId="localAuthorityBeNotified"
              i18nKey="Help Box - Incidents - Does the local authority need to be informed?"
            />
            <SelectElement
              printEnabled
              register={register}
              errors={errors}
              name="localAuthorityBeenNotified"
              label="Has the local authority been notified?"
              options={["Yes", "No"]}
              translateId="localAuthorityBeenNotified"
            />

            <SelectAndQmark
              printEnabled
              register={register}
              errors={errors}
              name="supervisionRateRespected"
              label="supervisionRateRespected"
              options={["Yes", "No"]}
              translateId="supervisionRateRespected"
              i18nKey="Help Box - Incidents - When the event occurred, was the staff ratio in compliance with regulations?"
            />

            <SelectElement
              printEnabled
              register={register}
              errors={errors}
              name="usualTeam"
              label="Usual team in the reception area?"
              options={["Yes", "No"]}
              translateId="usualTeam"
            />
            <TextArea
              printEnabled
              register={register}
              errors={errors}
              name="professionalNames"
              label="Name of all staff involved when the incident took place"
              translateId="professionalNames"
            />
            <CustomCheckboxes
              printEnabled
              register={register}
              errors={errors}
              name="immediateCorrectiveActionsArray"
              options={[
                { text: "Rearrangement of the space" },
                { text: "Awarness for the team, reminder of the protocol(s)" },
                { text: "Review of the field organization" },
                {
                  text: "Review of the schedules",
                },
                {
                  text: "Removal of the object/furniture that caused the accident or the risk of accident",
                },
                {
                  text: "Locking down of the risk area / securing pending maintenance/work intervention",
                },
                {
                  text: "Emergency intervention for corrective repair/maintenance (e.g.: window, anti-twist, sharp edges...)",
                },
                {
                  text: "none",
                },
              ]}
              label="Immediate corrective actions"
            />
            <CustomCheckboxes
              printEnabled
              register={register}
              errors={errors}
              name="riskRelatedEventArray"
              label="Risks related to the Health incident"
              options={[
                {
                  text: "Risk of parents negatively influencing other parents in the school",
                },
                {
                  text: "Risk that parents will give bad publicity (social networks, negative comments on the internet)",
                },
                { text: "Legal risk (lawsuit)" },
                { text: "Risk of partial closure of the school" },
                { text: "Risk of temporary closure of the school" },
                { text: "none" },
              ]}
            />
            <SelectElement
              printEnabled
              register={register}
              errors={errors}
              name="eventAvoidedSafer"
              label="eventAvoidedSafer"
              options={["Yes", "No", "Not applicable"]}
              translateId="eventAvoidedSafer"
            />
            {/* TODO:should be removed in the future */}

            <SelectElement
              printEnabled
              register={register}
              errors={errors}
              name="recurrence"
              label="Recurrence"
              options={[
                "No, isolated incident",
                "Yes, same type of incident happened in the last 3 months",
              ]}
              translateId="Recurrence"
            />
            {/* TODO: should be uncommented in the future */}
            {/* <SelectElement
              register={register}
              errors={errors}
              name="ageOfChild"
              label="Age of the child involved in the incident"
              options={[
                "2 months to 18 months",
                "19 months to 3 years",
                "3 years old",
                "4 years old",
                "5 years old",
                "6 years old",
              ]}
              translateId="ageOfChild"
            /> */}
          </div>
        </FormBorder>
        <div className="flex justify-end lg:px-[20px] mt-10">
          <button
            type={"submit"}
            className="px-5 py-2 rounded-full cursor-pointer text-white bg-[#1E408E]"
          >
            <Trans t={t}>Submit</Trans>
          </button>
          {loading && <Loaing />}
        </div>
      </form>
      <ConfirmModal
        isOpen={confirmModalVisible}
        setIsOpen={setConfirmModalVisible}
        onConfirm={resetForm}
      />
    </section>
  );
};

export default Incidents;
